import "./Paywall.css";
import { Button } from "@ui/Button";
import { ButtonLink } from "@ui/ButtonLink";
import { Modal } from "@ui/molecules/Modal/Modal";
import { VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import {
	createEffect,
	createResource,
	createSignal,
	For,
	Show,
} from "solid-js";
import { getPaymentMethods } from "@/client";
import { useAskPagePurchase } from "~/modules/askgvt/public-profile/components/useAskPagePurchase";
import { signInUrl } from "~/modules/shared/utils/auth";
import { addCardUrl, formatMoney } from "../askPageAccess";
import { sortPrices, TIER_COPY, TYPE_LABEL } from "../askPageTiers";
import { type AskPagePrice, askPageVars } from "../pageVars";

export type PaywallSubject = {
	handle: string;
	displayName: string;
	prices: AskPagePrice[];
	isSignedIn: boolean;
};

type Props = {
	open: () => boolean;
	onClose: () => void;
	initialType?: () => AskPagePrice["type"] | undefined;
	subject?: () => PaywallSubject | undefined;
	onPurchased?: () => void;
};

const subjectFromPage = (): PaywallSubject => {
	const vars = askPageVars();
	return {
		handle: vars.influencer_handle,
		displayName: vars.influencer_display_name,
		prices: vars.prices,
		isSignedIn: vars.isSignedIn,
	};
};

const emptySubject = (): PaywallSubject => ({
	handle: "",
	displayName: "",
	prices: [],
	isSignedIn: false,
});

export const Paywall = (props: Props) => {
	const subject = (): PaywallSubject =>
		props.subject ? (props.subject() ?? emptySubject()) : subjectFromPage();
	const purchase = useAskPagePurchase();
	const creator = () => subject().displayName;
	const prices = () => sortPrices(subject().prices);
	const cheapestType = (): AskPagePrice["type"] | undefined => {
		let cheapest: AskPagePrice | undefined;
		for (const price of prices()) {
			if (!cheapest || price.amount_minor < cheapest.amount_minor) {
				cheapest = price;
			}
		}
		return cheapest?.type;
	};
	const defaultType = (): AskPagePrice["type"] | undefined => {
		if (props.subject) {
			return cheapestType();
		}
		return (
			prices().find((price) => price.type === "subscription")?.type ??
			prices()[0]?.type
		);
	};

	const [selected, setSelected] = createSignal<
		AskPagePrice["type"] | undefined
	>(defaultType());
	const [methods] = createResource(
		() => subject().isSignedIn && props.open(),
		async (shouldLoad) => {
			if (!shouldLoad) {
				return [];
			}
			const res = await getPaymentMethods();
			return res.data?.payment_methods ?? [];
		},
	);

	createEffect(() => {
		if (!props.open()) {
			return;
		}
		const requested = props.initialType?.();
		if (requested && prices().some((price) => price.type === requested)) {
			setSelected(requested);
			return;
		}
		setSelected(defaultType());
	});

	const option = () => prices().find((price) => price.type === selected());
	const defaultCardId = () => {
		const loaded = methods() ?? [];
		return (loaded.find((method) => method.is_default) ?? loaded[0])?.id ?? "";
	};

	const buy = async () => {
		const chosen = option();
		const cardId = defaultCardId();
		if (!chosen || !cardId) {
			return;
		}
		const ok = await purchase.purchase({
			option: chosen,
			handle: subject().handle,
			paymentMethodId: cardId,
		});
		if (ok) {
			if (props.onPurchased) {
				props.onPurchased();
				return;
			}
			window.location.reload();
		}
	};

	const subscribeLabel = () => {
		if (purchase.state() === "authenticating") {
			return "Waiting for your bank…";
		}
		if (purchase.busy()) {
			return "Subscribing…";
		}
		return `Subscribe to ${creator()}`;
	};

	return (
		<Modal
			class="askgvt1-paywall"
			size="small"
			open={props.open}
			onClose={props.onClose}
			preTitle="Subscribers only"
			title={`Subscribe to ask ${creator()}`}
		>
			<VStack spacing="large">
				<Text size="small" color="fg2">
					This page is members-only past the public board. Pick a pass and your
					question goes through straight away.
				</Text>
				<Show
					when={prices().length > 0}
					fallback={
						<Text size="small" color="fg2">
							An active pass or subscription is required to ask this creator.
						</Text>
					}
				>
					<div
						class="askgvt1-paywall-options"
						role="radiogroup"
						aria-label="Access options"
					>
						<For each={prices()}>
							{(price) => (
								<label
									class="askgvt1-paywall-option"
									classList={{
										"askgvt1-paywall-option--selected":
											selected() === price.type,
									}}
								>
									<input
										type="radio"
										name="askgvt1-paywall-tier"
										value={price.type}
										checked={selected() === price.type}
										onChange={() => setSelected(price.type)}
										class="askgvt1-paywall-option__input"
									/>
									<VStack spacing="small">
										<Text weight="bold" color="ink">
											{TYPE_LABEL[price.type]}
										</Text>
										<Text size="small" color="fg2">
											{TIER_COPY[price.type].blurb}
										</Text>
									</VStack>
									<div class="askgvt1-paywall-option__price">
										<Text weight="bold" color="ink">
											{formatMoney(price.amount_minor, price.currency)}
										</Text>
										<Text size="small" color="fg3">
											{price.type === "subscription" ? "per month" : "one-time"}
										</Text>
									</div>
								</label>
							)}
						</For>
					</div>
				</Show>
				<Show when={purchase.error()}>
					<Text size="small" color="error">
						{purchase.error()}
					</Text>
				</Show>
				<VStack spacing="small">
					<Show
						when={subject().isSignedIn}
						fallback={
							<ButtonLink
								href={signInUrl()}
								variant="primary"
								theme="purple"
								fullWidth
							>
								Subscribe to {creator()}
							</ButtonLink>
						}
					>
						<Show
							when={!methods.loading && (methods() ?? []).length === 0}
							fallback={
								<Button
									type="button"
									variant="primary"
									theme="purple"
									fullWidth
									disabled={
										purchase.busy() ||
										methods.loading ||
										!option() ||
										!defaultCardId()
									}
									onClick={() => void buy()}
								>
									{subscribeLabel()}
								</Button>
							}
						>
							<ButtonLink
								href={addCardUrl(subject().handle)}
								variant="primary"
								theme="purple"
								fullWidth
							>
								Add a payment method
							</ButtonLink>
						</Show>
					</Show>
					<Show when={!subject().isSignedIn}>
						<ButtonLink
							href={signInUrl()}
							variant="tertiary"
							fullWidth
						>
							Log in
						</ButtonLink>
					</Show>
				</VStack>
				<Text size="xsmall" color="fg3" center>
					Cancel any time. {creator()} keeps 90%.
				</Text>
			</VStack>
		</Modal>
	);
};
